import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Meta } from '@angular/platform-browser';
import { firstValueFrom } from 'rxjs';
import { WindowRef } from '../services/window-ref.service';
import { WebLogger } from '../web-logger/WebLogger';
import { EnvironmentConfig } from '../environment.config';
import { UrlKey, UrlService } from '../services/url/url.service';

const ALLOW_ORIGIN = 'Access-Control-Allow-Origin';

export interface LoginResponse {
  sPOID: string;
  poid: string;
}

// Below Logic is only needed while Siteminder is still in use, can be removed once we are fully on RKE and Kong
async function isRkeEnabled(http: HttpClient, urlService: UrlService, config: EnvironmentConfig): Promise<boolean> {
  try {
    const gatekeeperUrl = urlService.getUrl(UrlKey.gatekeeeper_url, {
      featureIds: 'rke-enabled',
      env: config.gatekeeper_env
    });
    const features = await firstValueFrom(
      http.get<{ featureId: string; enabled: boolean }[]>(gatekeeperUrl)
    );
    return features.find(f => f.featureId === 'rke-enabled')?.enabled ?? false;
  } catch {
    WebLogger.log({ message: 'RKE gatekeeper fetch failed during session auth, defaulting to disabled', level: 'warn' });
    // TODO: Change the return to true once RKE is fully enabled in prod
    return false;
  }
}

/**
 * Calls the proxy server to check if cl_spoid set by Siteminder are in the headers to determine if a user is logged in.
 * If not, redirect user to logon url and reject so that content doesn't show. This only runs in the externally facing app.
 * Skipped when rke-enabled gatekeeper flag is true, as RKE handles authentication in that case.
 */
async function authorizeSession(http: HttpClient, windowRef: WindowRef, meta: Meta, config: EnvironmentConfig, urlService: UrlService): Promise<void> {
  // Runtime guard: ensure required config values are present
  if (!config.proxyServer) {
    throw new Error('Session authorization requires proxyServer in environment config');
  }
  if (!config.LOGON_URL) {
    throw new Error('Session authorization requires LOGON_URL in environment config');
  }
  if (!config.appUrl) {
    throw new Error('Session authorization requires appUrl in environment config');
  }

  if (await isRkeEnabled(http, urlService, config)) {
    WebLogger.log({ message: 'RKE enabled — skipping Siteminder session authorization', level: 'info' });
    return;
  }

  const $window: Window = windowRef.nativeWindow;
  const headers: HttpHeaders = new HttpHeaders().set(ALLOW_ORIGIN, config.appUrl);

  try {
    const data: LoginResponse = await firstValueFrom(
      http.get<LoginResponse>(`${config.proxyServer}login`, { headers: headers, withCredentials: true })
    );

    const { sPOID, poid } = data;
    meta.updateTag({ name: 'spoid', content: sPOID });
    meta.updateTag({ name: 'poid', content: poid });
  } catch (error) {
    WebLogger.log({
      message: 'Session authorization error',
      level: 'error',
      payload: { error: error instanceof Error ? { message: error.message, stack: error.stack } : String(error) }
    });
    $window.document.location.href = config.LOGON_URL;
    return Promise.reject();
  }
}

/**
 * Factory function to defer the execution of authorizeSession until invoked by Angular's APP_INITIALIZER.
 * This ensures session authorization happens before the app bootstraps.
 */
export function sessionAuthorizationFactory(http: HttpClient, windowRef: WindowRef, meta: Meta, config: EnvironmentConfig, urlService: UrlService): () => Promise<void> {
  return () => authorizeSession(http, windowRef, meta, config, urlService);
}
